import { joinTypeOptions } from '../../components/elem/select/StaticSelectObjects'
import {
    processIndependentCombinatorRules,
    restoreRuleToIndependentCombinatorForm,
} from './ruleConfig'
import { remove, transformQuery } from 'react-querybuilder'

const findDataSource = (config, dataSourceName) => {
    if (!config.dataSources) {
        return
    }
    return config.dataSources.find(
        (dataSource) => dataSource.name === dataSourceName
    )
}

const convertEmptyStringToNull = (val) => {
    return typeof val === 'string' && val.trim().length === 0 ? null : val
}
const convertReactSelectObjectToValue = (property) => {
    if (property && property !== null && property !== undefined) {
        if (
            typeof property === 'object' &&
            Object.keys(property).includes('value') &&
            Object.keys(property).includes('label')
        ) {
            return property.value
        }
    }
    return property
}

const amendConfigOptions = (reportConfigs) => {
    if (reportConfigs) {
        reportConfigs.primaryDataSourceName = convertReactSelectObjectToValue(
            reportConfigs.primaryDataSourceName
        )
        reportConfigs.databaseName = convertReactSelectObjectToValue(
            reportConfigs.databaseName
        )
        reportConfigs.filters =
            reportConfigs.filters && reportConfigs.filters.rules
                ? {
                      ...reportConfigs.filters,
                      rules: restoreRuleToIndependentCombinatorForm(
                          reportConfigs.filters.rules
                      ),
                  }
                : reportConfigs.filters
        reportConfigs.joins = reportConfigs.joins?.map((joinData) => {
            //for each rule group in joins make them have the combinator inside the rule
            let joinRules = joinData.filters && joinData.filters.rules
            if (joinRules && joinRules.length) {
                joinRules = restoreRuleToIndependentCombinatorForm(
                    joinData.filters.rules
                )
                return { ...joinData, filters: { rules: joinRules } }
            }
            return joinData
        })
    }
    return reportConfigs
}

const findColumn = (dataSource, columnName) => {
    return dataSource.columns.find((column) => column.name === columnName)
}

const addDataSource = (config, dataSource) => {
    if (!config.dataSources.map((x) => x.name).includes(dataSource.name)) {
        config.dataSources.push(dataSource)
    }
    return
}

const addColumn = (config, dataSourceName, column) => {
    const dataSource = findDataSource(config, dataSourceName)
    if (!dataSource.columns.map((x) => x.name).includes(column.name)) {
        dataSource.columns.push(column)
    }
    return
}

const removeDataSource = (config, dataSourceName) => {
    config.dataSources = config.dataSources.filter(
        (dataSource) => dataSource.name !== dataSourceName
    )
    return
}

const removeColumn = (config, dataSourceName, columnName) => {
    const dataSource = findDataSource(config, dataSourceName)
    dataSource.columns = dataSource.columns.filter(
        (column) => column.name !== columnName
    )
    return
}

const getTableFromSourceColumn = (src) =>
    src && src.includes('.') ? src.split('.')[0] : null

const removeTableReferences = (tableName, updatedReportConfig) => {
    if (!tableName) return

    updatedReportConfig.columns = updatedReportConfig.columns
        ? updatedReportConfig.columns.filter(
              (col) => getTableFromSourceColumn(col.sourceColumn) !== tableName
          )
        : []

    updatedReportConfig.orderBy = updatedReportConfig.orderBy
        ? updatedReportConfig.orderBy.filter(
              (ob) => getTableFromSourceColumn(ob.sourceColumn) !== tableName
          )
        : []

    updatedReportConfig.groupBy = updatedReportConfig.groupBy
        ? updatedReportConfig.groupBy.filter(
              (gb) => getTableFromSourceColumn(gb.sourceColumn) !== tableName
          )
        : []

    updatedReportConfig.dataSources = updatedReportConfig.dataSources
        ? updatedReportConfig.dataSources.filter((ds) => ds.name !== tableName)
        : []

    if (updatedReportConfig.filters) {
        let transformedQuery = updatedReportConfig.filters
        let removeRules = []

        transformQuery(updatedReportConfig.filters, {
            ruleProcessor: (rule) => {
                if (rule.tableAlias === tableName || rule.table === tableName) {
                    removeRules.push(rule)
                }
                return rule
            },
        })

        removeRules.forEach((rule) => {
            transformedQuery = remove(transformedQuery, rule.path)
        })

        updatedReportConfig.filters = transformedQuery
    }
}

const expressionTypeOptions = [
    { value: 'MULTIPLY', label: 'Multiply' },
    { value: 'DIVIDE', label: 'Divide' },
    { value: 'CAST', label: 'Cast' },
    { value: 'ISNULL', label: 'Value if Empty' },
]

const reportingLayoutConfig = [
    { name: 'Create Report', path: '/reporting/create-report' },
    { name: 'Report Information', component: '/reporting/report-information' },
    { name: 'Joins', path: 'joins' },
    { name: 'Select Columns', path: 'select-columns' },
    { name: 'Where Clause', path: 'where' },
    { name: 'Group By Columns', path: 'groupby-columns' },
    { name: 'Order By Columns', path: 'orderby-columns' },
    { name: 'Preview', path: 'preview' },
]

const getVisibleDataSources = (
    reportOptions,
    reportConfig,
    newDatabaseName = null
) => {
    var dataSources = []
    const databaseName = newDatabaseName
        ? newDatabaseName
        : reportConfig.databaseName
    // if there are dataSources that exist, add them to the list.
    // otherwise, if there is a primary data source, add that
    // to the list and start it
    if (
        databaseName &&
        reportOptions &&
        reportOptions.dataSources &&
        reportOptions.dataSources.length
    ) {
        const visibleDataSources = reportOptions.dataSources.filter(
            (dataSource) => dataSource.DATABASE_NAME === databaseName
        )
        dataSources = visibleDataSources.map((x) => ({
            ...x,
            label: x.TABLE_NAME,
            value: x.TABLE_NAME,
        }))
    } else {
        dataSources = []
    }
    if (reportConfig && reportConfig.primaryDataSourceName) {
        const primaryDataSource = reportConfig.primaryDataSourceName
        const primaryDataSourceExists = reportConfig.dataSources
            .map((dataSource) => dataSource.name)
            .includes(primaryDataSource)
        if (!primaryDataSourceExists) {
            dataSources.push({
                label: primaryDataSource,
                value: primaryDataSource,
            })
        }
    }
    return dataSources
}

const getVisibleSelectedDataSources = (reportOptions, reportConfig) => {
    if (
        reportOptions &&
        reportConfig &&
        reportConfig.primaryDataSourceName &&
        reportConfig.joins
    ) {
        const primaryDataSource = reportConfig.primaryDataSourceName
        const dataSources = [
            { name: primaryDataSource, alias: null },
            ...reportConfig.joins.map((join) => ({
                name: join.table2,
                alias: join.alias,
            })),
        ]

        // TODO: make the label here map to the alias if it exists
        return dataSources.map(({ name, alias }) => ({
            alias: convertEmptyStringToNull(alias),
            label: convertEmptyStringToNull(alias) || name,
            value: name,
        }))
    }
    return []
}

const getVisibleColumns = (reportOptions, visibleDataSources) => {
    if (
        reportOptions &&
        reportOptions.columns &&
        visibleDataSources &&
        visibleDataSources.length
    ) {
        const columns = reportOptions.columns.filter((column) =>
            visibleDataSources
                .map((dataSource) => dataSource.value)
                .includes(column.TABLE_NAME)
        )
        // TODO: make the label here map to the alias if it exists
        return columns.map((column) => ({
            ...column,
            label: column.COLUMN_NAME,
            value: column.COLUMN_NAME,
        }))
    }
    return []
}

const getTags = (reportOptions) => {
    if (reportOptions && reportOptions.tags && reportOptions.tags.length) {
        return reportOptions.tags.map((x) => ({ label: x.tag, value: x.tag }))
    }
    return []
}

const getReportSelectOptions = (
    reportOptions,
    reportConfig,
    newDatabaseName = null
) => {
    const databaseName = newDatabaseName
        ? newDatabaseName
        : reportConfig.databaseName
    let selectOptions = {
        databases: [],
        dataSources: [],
        tags: [],
        joinType: [],
        selectedDataSources: [],
        selectedDataSourceColumns: [],
    }
    // databases are visible for all reports
    selectOptions.databases = reportOptions.databases
        ? reportOptions.databases.map((databaseName) => ({
              label: databaseName,
              value: databaseName,
          }))
        : []

    // tags are visible for all reports, before database is selected
    selectOptions.tags = getTags(reportOptions)

    // join types - joins -> joinType
    selectOptions.joinType = joinTypeOptions
    selectOptions.expressionType = expressionTypeOptions

    if (databaseName) {
        // datasources are populated based on selected database
        const visibleDataSources = getVisibleDataSources(
            reportOptions,
            reportConfig,
            newDatabaseName
        )
        selectOptions.dataSources = visibleDataSources

        // dataSources that have been incorporated into the config
        selectOptions.selectedDataSources = getVisibleSelectedDataSources(
            reportOptions,
            reportConfig
        )

        // column selection options that are based on the selected dataSources
        selectOptions.selectedDataSourceColumns = getVisibleColumns(
            reportOptions,
            visibleDataSources
        )
    }
    // console.log('reportOptions:', reportOptions, 'reportConfig:', reportConfig, 'selectOptions:', selectOptions)
    return selectOptions
}

const getTableNameWithIdentifier = (identifier, dataSources) =>
    dataSources &&
    Array.isArray(dataSources) &&
    dataSources.find(
        (x) => identifier === (x.alias !== null ? x.alias : x.value)
    )?.value

const getOrphanedTables = (initialTable, joins, skipIndex) => {
    const orphanedTables = new Set([initialTable])
    joins.forEach((join, index) => {
        if (index === skipIndex) return

        if (
            orphanedTables.has(join.table1) &&
            (!orphanedTables.has(join.alias) ||
                !orphanedTables.has(join.table2))
        ) {
            orphanedTables.add(join.alias || join.table2)
        }
    })
    return orphanedTables
}

const removeJoinRecursively = (joinIndex, reportConfig) => {
    const joinToRemove = reportConfig.joins[joinIndex]
    if (!joinToRemove) return []

    // Get all tables that would become orphaned by removing this join
    const orphanedTables = getOrphanedTables(
        joinToRemove.alias || joinToRemove.table2,
        reportConfig.joins,
        joinIndex
    )

    // Filter out the target join and any joins referencing orphaned tables
    const filteredJoins = reportConfig.joins.filter((join, index) => {
        if (index === joinIndex) return false
        return (
            !orphanedTables.has(join.alias) &&
            !orphanedTables.has(join.table2) &&
            !orphanedTables.has(join.table1)
        )
    })
    reportConfig.joins = filteredJoins
    return orphanedTables
}

const defaultReportConfig = {
    reportName: '',
    reportDescription: '',
    databaseName: '',
    primaryDataSourceName: '',
    dataSources: [],
    limit: null,
    hasDistinct: false,
    orderBy: [],
    joins: [],
    filters: {
        not: false,
        rules: [],
    },
    columns: [],
    tags: [],
    groupBy: [],
    whereGroupsIncluded: false,
}

const updateDataSourcesFromFormData = (
    formData,
    reportConfig,
    reportOptions
) => {
    const dataSources = formData.dataSources ? formData.dataSources : []
    const primaryDataSourceName = formData.primaryDataSourceName
    if (primaryDataSourceName) {
        let primaryDataSource = findDataSource(
            reportOptions,
            primaryDataSourceName
        )
        if (!primaryDataSource) {
            if (
                !dataSources.map((x) => x.name).includes(primaryDataSourceName)
            ) {
                primaryDataSource = {
                    name: primaryDataSourceName,
                    alias: null,
                    isPrimary: true,
                }
                dataSources.push(primaryDataSource)
            }
        }
    }
    if (formData.joins) {
        formData.joins.forEach((join) => {
            // if (!dataSources.map((x) => x.name).includes(join.table1)) {
            //     const dataSource = findDataSource(reportOptions, join.table1)
            //     if (!dataSource) {
            //         const t1DataSource = {
            //             name: join.table1,
            //             isPrimary: false,
            //             alias: null,
            //         }
            //         dataSources.push(t1DataSource)
            //     }
            // }
            if (
                !dataSources.map((x) => x.name).includes(join.table2) ||
                !dataSources.map((x) => x.name).includes(join.alias)
            ) {
                const dataSource = findDataSource(reportOptions, join.table2)
                if (!dataSource) {
                    const t2DataSource = {
                        name: join.table2,
                        isPrimary: false,
                        alias: join.alias !== '' ? join.alias : null,
                    }
                    dataSources.push(t2DataSource)
                }
            }
        })
    }
    return dataSources
}

const resolveSelectValues = (reportAttributes) => {
    //iterate through all attributes and convert react-select values from this format { label: , value: } into plain format
    for (const attribute in reportAttributes) {
        let currentAttr = reportAttributes[attribute]
        if (currentAttr) {
            if (currentAttr instanceof Array) {
                currentAttr.forEach((obj) => {
                    if (obj && typeof obj === 'object') {
                        for (const currentObj in obj) {
                            obj[currentObj] = convertReactSelectObjectToValue(
                                obj[currentObj]
                            )
                        }
                    }
                })
            } else {
                reportAttributes[attribute] =
                    convertReactSelectObjectToValue(currentAttr)
            }
        }
    }
}

const generateReportAttributesFromFormData = (
    formData,
    reportConfig,
    reportOptions
) => {
    try {
        const reportAttributes = {
            primaryDataSourceName: formData.primaryDataSourceName
                ? formData.primaryDataSourceName
                : reportConfig.primaryDataSourceName,
            databaseName: formData.databaseName
                ? formData.databaseName
                : reportConfig.databaseName,
            dataSources: formData.dataSources
                ? formData.dataSources
                : defaultReportConfig.dataSources,
            joins: formData.joins ? formData.joins : defaultReportConfig.joins,
            filters:
                formData.filters && formData.filters.rules
                    ? {
                          ...formData.filters,
                          rules: processIndependentCombinatorRules(
                              formData.filters.rules
                          ),
                      }
                    : defaultReportConfig.filters,
            columns: formData.columns
                ? formData.columns
                : defaultReportConfig.columns,
            calculatedColumns: formData.calculatedColumns
                ? formData.calculatedColumns
                : defaultReportConfig.calculatedColumns,
            tags: formData.tags
                ? formData.tags
                      .filter((x) => x !== null && x !== undefined)
                      .map((x) => x.value)
                : defaultReportConfig.tags,
            limit: formData.limit ? formData.limit : defaultReportConfig.limit,
            hasDistinct: formData.hasDistinct
                ? formData.hasDistinct
                : defaultReportConfig.hasDistinct,
            groupBy: formData.groupBy
                ? formData.groupBy.filter(gb => formData.columns.some(c => c.sourceColumn === gb.sourceColumn))
                : defaultReportConfig.groupBy,
            orderBy: formData.orderBy
                ? formData.orderBy
                : defaultReportConfig.orderBy,
            whereGroupsIncluded:
                formData.whereGroupsIncluded !== undefined
                    ? formData.whereGroupsIncluded
                    : defaultReportConfig.whereGroupsIncluded,
        }
        reportAttributes.joins = reportAttributes.joins.map((joinData) => {
            //for each rule group in joins make them have the combinator inside the rule
            let joinRules = joinData.filters && joinData.filters.rules
            if (joinRules && joinRules.length) {
                const t1 = convertReactSelectObjectToValue(joinData.table1)
                const t2 = convertReactSelectObjectToValue(joinData.table2)
                const tables = `${t1},${t2}`
                joinRules = processIndependentCombinatorRules(
                    joinData.filters.rules,
                    tables
                )
                return { ...joinData, filters: { rules: joinRules } }
            }
            return joinData
        })
        reportAttributes.dataSources = updateDataSourcesFromFormData(
            formData,
            reportConfig,
            reportOptions
        )
        resolveSelectValues(reportAttributes)
        // console.log('generateReportAttributesFromFormData: reportAttributes - ', reportAttributes, 'formData - ', formData)
        return JSON.stringify(reportAttributes)
    } catch (e) {
        throw new Error('Error generating report attributes from form data')
    }
}

const generatePostRequestFromFormData = (
    formData,
    reportConfig,
    reportOptions
) => {
    const reportAttributes = generateReportAttributesFromFormData(
        formData,
        reportConfig,
        reportOptions
    )
    const postRequestData = {
        reportName: formData.reportName,
        reportDescription: formData.reportDescription,
        databaseName: convertReactSelectObjectToValue(
            formData.databaseName
                ? formData.databaseName
                : reportConfig.databaseName
        ),
        reportAttributes: reportAttributes,
    }
    // console.log('generatePostRequestFromFormData: final post request data - ', postRequestData)
    return postRequestData
}

const validateUniqueReportName = (
    reportName,
    existingReports,
    currentReport = null
) => {
    if (!reportName || typeof reportName !== 'string') {
        return 'Required field'
    }

    const trimmedName = reportName.trim()
    if (!trimmedName) {
        return 'Required field'
    }

    const isDuplicate =
        existingReports &&
        existingReports.some(
            (report) =>
                report.ReportName &&
                report.ReportId !==
                    (currentReport !== null ? currentReport.ReportId : 0) &&
                report.ReportName.toLowerCase().trim() ===
                    trimmedName.toLowerCase()
        )

    return isDuplicate ? 'Duplicate values are not allowed' : true
}

//returns false if it fails, only works for value and array of value not objects
const validateUniqueness = (value, existingValues) => {
    if (value === null || value === undefined || typeof value !== 'string') {
        return true // nulls are not considered for duplication
    }
    const trimmedName = value.trim()

    const isDuplicate =
        existingValues &&
        existingValues.filter(
            (v) => v && v.toLowerCase().trim() === trimmedName.toLowerCase()
        )

    return isDuplicate && isDuplicate.length > 1
        ? 'Duplicate values are not allowed'
        : true
}

const filterDataSourceOptions = (dataSources) => {
    const allowedTables = [
        'Address',
        'Agent',
        'Communication',
        'Facility',
        'FacilityAlias',
        'Parameter',
        'Result',
        'ResultMethod',
        'Sample',
        'SampleEntity',
        'RefCodes',
        'RefAlgalMasterTaxa',
        'RefBenthicMasterTaxa',
        'RefCounty',
        'RefPhysParameter',
        'Coll',
        'CollParameter',
        'ExternalTranslate',
        'ExternalTranslateNoMatch',
    ]

    const allowedViewPrefix = ['vwRef', 'vwDashboard']

    if (!Array.isArray(dataSources)) return []

    return dataSources.filter((source) => {
        const name = source.value || source.label || source.name || ''
        return (
            allowedTables.includes(name) ||
            allowedViewPrefix.some((prefix) => name.startsWith(prefix))
        )
    })
}

const shouldAutoSelectDatabase = (selectedDatabase, databaseOptions) => {
    return (
        databaseOptions.length === 1 &&
        selectedDatabase?.value === databaseOptions[0].value
    )
}

const capitalizeWords = (str) => {
    if (!str || typeof str !== 'string') return str
    return str.replace(/\b\w/g, (letter) => letter.toUpperCase())
}

const noApostropheOrPercentRegex = /^[^'%]*$/

const isDecimalWithinPrecision = (value, precision, scale) => {
    const str = value.toString()
    const [intPart, decPart = ''] = str.split('.')

    const totalDigits = intPart.length + decPart.length
    return totalDigits <= precision && decPart.length <= scale
}

export {
    findDataSource,
    findColumn,
    addDataSource,
    addColumn,
    removeDataSource,
    removeColumn,
    reportingLayoutConfig,
    defaultReportConfig,
    getReportSelectOptions,
    generatePostRequestFromFormData,
    generateReportAttributesFromFormData,
    amendConfigOptions,
    convertReactSelectObjectToValue,
    validateUniqueReportName,
    filterDataSourceOptions,
    shouldAutoSelectDatabase,
    validateUniqueness,
    convertEmptyStringToNull,
    capitalizeWords,
    noApostropheOrPercentRegex,
    isDecimalWithinPrecision,
    removeTableReferences,
    removeJoinRecursively,
    getTableNameWithIdentifier,
}
